import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  getClientCredits,
  listCreditPackages,
  createCreditCheckoutSession,
  listCreditPurchases,
  listCreditUsage,
  CreditPackage,
  CreditPurchaseItem,
  CreditUsageItem,
} from '../lib/callables';
import './Billing.css';
import { User } from 'firebase/auth';

type BillingProps = {
  user: User;
};

export default function Billing({ user }: BillingProps) {
  const { colors } = useTheme();
  const location = useLocation();
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyTab, setHistoryTab] = useState<'purchases' | 'usage'>('purchases');
  const [purchases, setPurchases] = useState<CreditPurchaseItem[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [usage, setUsage] = useState<CreditUsageItem[]>([]);
  const [usageLoading, setUsageLoading] = useState(true);
  const [expandedUsage, setExpandedUsage] = useState<Set<string>>(new Set());

  const toggleUsageKey = (key: string) => {
    setExpandedUsage((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [credits, pkgs] = await Promise.all([
        getClientCredits(),
        listCreditPackages(),
      ]);
      setCreditBalance(credits.creditBalance || 0);
      setPackages([...pkgs].sort((a, b) => (a.priceUsd || 0) - (b.priceUsd || 0)));
      console.log('Loaded packages:', pkgs);
      if (pkgs.length === 0) {
        setError('No active credit packages found. Packages must have "Active" checked in the Admin Portal to appear here.');
      }
    } catch (err: any) {
      console.error('Failed to load billing data:', err);
      setError(err.message || 'Failed to load credit packages. Please try again.');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPurchasesLoading(true);
        const result = await listCreditPurchases();
        if (!cancelled) setPurchases(result.items || []);
      } catch (err) {
        console.error('Failed to load purchase history:', err);
        if (!cancelled) setPurchases([]);
      } finally {
        if (!cancelled) setPurchasesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setUsageLoading(true);
        const result = await listCreditUsage();
        if (!cancelled) setUsage(result.items || []);
      } catch (err) {
        console.error('Failed to load usage history:', err);
        if (!cancelled) setUsage([]);
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const packageNameById = useMemo(() => {
    const m: Record<string, string> = {};
    packages.forEach((p) => { m[p.id] = p.name; });
    return m;
  }, [packages]);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const typeLabel = (t: string) =>
    t === 'annotatedPhoto' ? 'Annotated Photos' : t === 'rawVideo' ? 'Raw Videos' : 'Raw Photos';

  // Each historical purchase is ONE dated row. Expanding shows that purchase's
  // own Category > Subcategory > Task breakdown. Purchases are never merged.
  const usagePurchases = useMemo(() => {
    return usage.map((rec) => {
      type Leaf = { name: string; count: number; credits: number };
      type Sub = { name: string; count: number; credits: number; tasks: Record<string, Leaf> };
      type Cat = { name: string; count: number; credits: number; subs: Record<string, Sub> };
      const cats: Record<string, Cat> = {};
      for (const g of rec.groups) {
        if (!cats[g.category]) cats[g.category] = { name: g.category, count: 0, credits: 0, subs: {} };
        const C = cats[g.category];
        C.count += g.count; C.credits += g.credits;
        if (!C.subs[g.subcategory]) C.subs[g.subcategory] = { name: g.subcategory, count: 0, credits: 0, tasks: {} };
        const S = C.subs[g.subcategory];
        S.count += g.count; S.credits += g.credits;
        if (!S.tasks[g.taskTitle]) S.tasks[g.taskTitle] = { name: g.taskTitle, count: 0, credits: 0 };
        const L = S.tasks[g.taskTitle];
        L.count += g.count; L.credits += g.credits;
      }
      return {
        id: rec.id,
        date: rec.spentAt,
        typeName: typeLabel(rec.type),
        totalItems: rec.totalItems,
        totalCredits: rec.totalCredits,
        cats: Object.values(cats).map((C) => ({
          name: C.name, count: C.count, credits: C.credits,
          subs: Object.values(C.subs).map((S) => ({
            name: S.name, count: S.count, credits: S.credits,
            tasks: Object.values(S.tasks),
          })),
        })),
      };
    });
  }, [usage]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status === 'success') {
      setStatusMessage('Payment received. Your credits will update shortly.');
      setError(null);
      // Reload credits after short delay
      const timeout = setTimeout(() => {
        loadData();
      }, 3000);
      return () => clearTimeout(timeout);
    }
    if (status === 'cancel') {
      setError('Payment was cancelled.');
      setStatusMessage(null);
    }
  }, [location.search, loadData]);

  const handlePurchase = async (pkg: CreditPackage) => {
    setProcessing(pkg.id);
    setError(null);
    setStatusMessage(null);
    try {
      const origin = window.location.origin;
      const successUrl = `${origin}/billing?status=success`;
      const cancelUrl = `${origin}/billing?status=cancel`;
      const session = await createCreditCheckoutSession(pkg.id, successUrl, cancelUrl);
      if (session.url) {
        window.location.href = session.url;
      } else {
        throw new Error('No checkout session URL returned.');
      }
    } catch (err: any) {
      console.error('Failed to create checkout session:', err);
      if (err.code === 'failed-precondition') {
        setError('This package is not configured for purchase yet. Please contact support.');
      } else {
        setError(err.message || 'Unable to start checkout. Please try again.');
      }
      setProcessing(null);
    }
  };

  return (
    <div className="billing-page" style={{ color: colors.text }}>
      <div className="billing-header">
        <div>
          <h1>Buy Credits</h1>
          <p className="billing-subtitle" style={{ color: colors.secondary }}>
            Purchase credit packs to continue labeling and exporting data.
          </p>
        </div>
        <div className="billing-balance" style={{ background: colors.card, borderColor: colors.border }}>
          <span style={{ color: colors.secondary }}>Credit Balance</span>
          <strong>{loading ? 'Loading…' : creditBalance.toLocaleString()}</strong>
        </div>
      </div>

      {statusMessage && (
        <div className="billing-alert billing-alert--success">
          {statusMessage}
        </div>
      )}

      {error && (
        <div className="billing-alert billing-alert--error">
          {error}
        </div>
      )}

      <div className="billing-packages">
        {loading ? (
          <div className="billing-loading">Loading packages…</div>
        ) : packages.length === 0 ? (
          <div className="billing-empty" style={{ background: colors.card, borderColor: colors.border }}>
            No credit packages are available yet. Please contact Pixano support.
          </div>
        ) : (
          packages.map((pkg) => (
            <div
              key={pkg.id}
              className="billing-package-card"
              style={{ background: colors.card, borderColor: colors.border }}
            >
              <div className="billing-package-top">
                <h3>{pkg.name}</h3>
                {pkg.description && (
                  <p className="billing-package-desc" style={{ color: colors.secondary }}>
                    {pkg.description}
                  </p>
                )}
              </div>

              <div className="billing-package-product">
                <div className="billing-package-credits" style={{ color: colors.primary }}>
                  {pkg.credits.toLocaleString()}
                </div>
                <div className="billing-package-unit" style={{ color: colors.secondary }}>
                  Credits
                </div>
              </div>

              <div className="billing-pay-block" style={{ borderColor: colors.border }}>
                <div className="billing-pay-label" style={{ color: colors.secondary }}>
                  Pay with USD
                </div>
                <div className="billing-package-price">${pkg.priceUsd.toFixed(2)}</div>
                <button
                  className="billing-package-btn"
                  style={{ background: colors.primary, color: '#050505' }}
                  disabled={processing === pkg.id}
                  onClick={() => handlePurchase(pkg)}
                >
                  {processing === pkg.id ? 'Processing…' : 'Buy with USD'}
                </button>
              </div>

              {pkg.pixShow && (
                <div className="billing-pay-block" style={{ borderColor: colors.border }}>
                  {(pkg.pixDiscountPercent ?? 0) > 0 && (
                    <div
                      className="billing-pix-badge"
                      style={{ background: colors.primary + '20', color: colors.primary }}
                    >
                      Save {pkg.pixDiscountPercent}%
                    </div>
                  )}
                  <div className="billing-pay-label" style={{ color: colors.secondary }}>
                    Pay with PIX
                  </div>
                  <div className="billing-package-price">
                    ${(pkg.priceUsd * (1 - (pkg.pixDiscountPercent ?? 0) / 100)).toFixed(2)}
                  </div>
                  <button
                    className="billing-package-btn billing-pix-btn"
                    style={{ borderColor: colors.border, color: colors.secondary }}
                    disabled
                  >
                    Coming Soon
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="billing-history">
        <h2 className="billing-history-heading" style={{ color: colors.text }}>History</h2>
        <div className="billing-history-tabs">
          <button
            className={`billing-history-tab ${historyTab === 'purchases' ? 'active' : ''}`}
            style={{
              color: historyTab === 'purchases' ? colors.primary : colors.secondary,
              borderColor: historyTab === 'purchases' ? colors.primary : 'transparent',
            }}
            onClick={() => setHistoryTab('purchases')}
          >
            Purchase History
          </button>
          <button
            className={`billing-history-tab ${historyTab === 'usage' ? 'active' : ''}`}
            style={{
              color: historyTab === 'usage' ? colors.primary : colors.secondary,
              borderColor: historyTab === 'usage' ? colors.primary : 'transparent',
            }}
            onClick={() => setHistoryTab('usage')}
          >
            Credits Used
          </button>
        </div>

        {historyTab === 'purchases' ? (
          purchasesLoading ? (
            <div className="billing-history-empty" style={{ color: colors.secondary }}>
              Loading purchase history…
            </div>
          ) : purchases.length === 0 ? (
            <div className="billing-history-empty" style={{ color: colors.secondary }}>
              No credit purchases yet.
            </div>
          ) : (
            <div className="billing-history-table" style={{ borderColor: colors.border }}>
              <div className="billing-history-row billing-history-head" style={{ color: colors.secondary, borderColor: colors.border }}>
                <span>Date</span>
                <span>Package</span>
                <span className="billing-history-num">Credits</span>
                <span className="billing-history-num">Amount</span>
              </div>
              {purchases.map((p) => (
                <div
                  key={p.id}
                  className="billing-history-row"
                  style={{ color: colors.text, borderColor: colors.border }}
                >
                  <span>{formatDate(p.purchasedAt)}</span>
                  <span>{packageNameById[p.packageId] || p.packageId || '—'}</span>
                  <span className="billing-history-num" style={{ color: colors.primary }}>
                    +{p.credits.toLocaleString()}
                  </span>
                  <span className="billing-history-num">${p.amountUsd.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )
        ) : usageLoading ? (
          <div className="billing-history-empty" style={{ color: colors.secondary }}>
            Loading usage history…
          </div>
        ) : usagePurchases.length === 0 ? (
          <div className="billing-history-empty" style={{ color: colors.secondary }}>
            No credits spent yet. When you purchase data from the marketplace,
            a breakdown by category and task will appear here.
          </div>
        ) : (
          <div className="billing-tree" style={{ borderColor: colors.border }}>
            <div className="billing-tree-row billing-tree-head" style={{ color: colors.secondary, borderColor: colors.border }}>
              <span className="billing-tree-date">Date</span>
              <span className="billing-tree-name">Package</span>
              <span className="billing-tree-count">Items</span>
              <span className="billing-tree-credits">Credits</span>
            </div>
            {usagePurchases.map((P) => {
              const pk = `p:${P.id}`;
              const pOpen = expandedUsage.has(pk);
              return (
                <div key={pk} className="billing-tree-block">
                  <div
                    className="billing-tree-row billing-tree-l0"
                    style={{ color: colors.text, borderColor: colors.border }}
                    onClick={() => toggleUsageKey(pk)}
                  >
                    <span className="billing-tree-date">{formatDate(P.date)}</span>
                    <span className="billing-tree-name">
                      <span className="billing-tree-caret">{pOpen ? '▾' : '▸'}</span>
                      {P.typeName}
                    </span>
                    <span className="billing-tree-count" style={{ color: colors.secondary }}>{P.totalItems.toLocaleString()} items</span>
                    <span className="billing-tree-credits" style={{ color: colors.primary }}>{P.totalCredits.toLocaleString()} credits</span>
                  </div>
                  {pOpen && P.cats.map((C) => {
                    const ck = `${pk}|c:${C.name}`;
                    const cOpen = expandedUsage.has(ck);
                    return (
                      <div key={ck}>
                        <div
                          className="billing-tree-row billing-tree-l1"
                          style={{ color: colors.text, borderColor: colors.border }}
                          onClick={() => toggleUsageKey(ck)}
                        >
                          <span className="billing-tree-date"></span>
                          <span className="billing-tree-name">
                            <span className="billing-tree-caret">{cOpen ? '▾' : '▸'}</span>
                            {C.name}
                          </span>
                          <span className="billing-tree-count" style={{ color: colors.secondary }}>{C.count.toLocaleString()} items</span>
                          <span className="billing-tree-credits" style={{ color: colors.primary }}>{C.credits.toLocaleString()} credits</span>
                        </div>
                        {cOpen && C.subs.map((S) => {
                          const sk = `${ck}|s:${S.name}`;
                          const sOpen = expandedUsage.has(sk);
                          return (
                            <div key={sk}>
                              <div
                                className="billing-tree-row billing-tree-l2"
                                style={{ color: colors.text, borderColor: colors.border }}
                                onClick={() => toggleUsageKey(sk)}
                              >
                                <span className="billing-tree-date"></span>
                                <span className="billing-tree-name">
                                  <span className="billing-tree-caret">{sOpen ? '▾' : '▸'}</span>
                                  {S.name}
                                </span>
                                <span className="billing-tree-count" style={{ color: colors.secondary }}>{S.count.toLocaleString()} items</span>
                                <span className="billing-tree-credits" style={{ color: colors.primary }}>{S.credits.toLocaleString()} credits</span>
                              </div>
                              {sOpen && S.tasks.map((L) => (
                                <div
                                  key={`${sk}|k:${L.name}`}
                                  className="billing-tree-row billing-tree-l3"
                                  style={{ color: colors.text, borderColor: colors.border }}
                                >
                                  <span className="billing-tree-date"></span>
                                  <span className="billing-tree-name">{L.name}</span>
                                  <span className="billing-tree-count" style={{ color: colors.secondary }}>{L.count.toLocaleString()} items</span>
                                  <span className="billing-tree-credits" style={{ color: colors.primary }}>{L.credits.toLocaleString()} credits</span>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
